import { ElementRole } from '../types';

const MAX_NAME_LENGTH = 48;

const ROLE_BY_TAG: Readonly<Record<string, ElementRole>> = {
  BUTTON: 'button',
  SUMMARY: 'button',
  SELECT: 'combobox',
  TEXTAREA: 'textbox',
  TR: 'row',
  TD: 'cell',
  TH: 'columnheader',
  OPTION: 'option',
};

const ROLE_BY_INPUT_TYPE: Readonly<Record<string, ElementRole>> = {
  button: 'button',
  checkbox: 'checkbox',
  email: 'textbox',
  number: 'spinbutton',
  password: 'textbox',
  radio: 'radio',
  range: 'slider',
  reset: 'button',
  search: 'searchbox',
  submit: 'button',
  text: 'textbox',
};

const KNOWN_ROLES: ReadonlySet<string> = new Set<ElementRole>([
  ...Object.values(ROLE_BY_TAG),
  ...Object.values(ROLE_BY_INPUT_TYPE),
  'cell',
  'dialog',
  'generic',
  'grid',
  'gridcell',
  'group',
  'heading',
  'link',
  'list',
  'listbox',
  'listitem',
  'main',
  'menu',
  'menuitem',
  'menuitemcheckbox',
  'menuitemradio',
  'navigation',
  'radiogroup',
  'region',
  'row',
  'rowgroup',
  'rowheader',
  'switch',
  'tab',
  'tablist',
  'tabpanel',
  'toolbar',
  'tree',
  'treeitem',
]);

export const resolveRole = (element: Element): ElementRole | null => {
  const explicit = element.getAttribute('role');
  if (explicit) {
    return KNOWN_ROLES.has(explicit) ? (explicit as ElementRole) : 'generic';
  }
  if (element.tagName === 'A') {
    return element.hasAttribute('href') ? 'link' : null;
  }
  if (element.tagName === 'INPUT') {
    const type = (element as HTMLInputElement).type.toLowerCase();
    return ROLE_BY_INPUT_TYPE[type] ?? 'textbox';
  }
  return ROLE_BY_TAG[element.tagName] ?? null;
};

const collapse = (value: string): string => value.replace(/\s+/g, ' ').trim();

const visibleText = (element: Element): string => {
  const clone = element.cloneNode(true) as Element;
  clone
    .querySelectorAll('[aria-hidden="true"], svg, script, style')
    .forEach(hidden => hidden.remove());
  clone.querySelectorAll('*').forEach(child => child.append(' '));
  return collapse(clone.textContent ?? '');
};

const labelledByText = (element: Element): string => {
  const ids = element.getAttribute('aria-labelledby');
  if (!ids) {
    return '';
  }
  const doc = element.ownerDocument;
  return collapse(
    ids
      .split(/\s+/)
      .map(id => doc.getElementById(id)?.textContent ?? '')
      .join(' '),
  );
};

const associatedLabelText = (element: Element): string => {
  const labels = (element as HTMLInputElement).labels;
  if (labels && labels.length > 0) {
    return visibleText(labels[0]);
  }
  const ancestor = element.closest('label');
  return ancestor ? visibleText(ancestor) : '';
};

const STRUCTURAL_TAGS = ['TR', 'TABLE', 'TBODY', 'THEAD', 'TFOOT'];

const MASK_SELECTOR = '[data-csr-mask], [data-csr-block]';

const IDENTITY_PATTERNS = [
  /@/,
  /^[a-z][a-z0-9-]*\/[A-Za-z0-9._-]+$/,
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/i,
  /\b[0-9A-HJKMNP-TV-Z]{26}\b/,
  /\b\d{4}-\d{2}-\d{2}\b/,
  /^\d+$/,
  /\b\d{6,}\b/,
  /\b[0-9a-f]{16,}\b/i,
];

export const hasMaskedContent = (element: Element): boolean => {
  if (element.closest(MASK_SELECTOR) || element.querySelector(MASK_SELECTOR)) {
    return true;
  }
  return !!element.closest('tr')?.querySelector(MASK_SELECTOR);
};

export const isIdentityLike = (value: string): boolean =>
  IDENTITY_PATTERNS.some(pattern => pattern.test(value));

export const isSensitiveText = (source: Element, value: string): boolean =>
  !!value && (hasMaskedContent(source) || isIdentityLike(value));

export const resolveAccessibleName = (element: Element): string => {
  const candidates = [
    labelledByText(element),
    collapse(element.getAttribute('aria-label') ?? ''),
    associatedLabelText(element),
    STRUCTURAL_TAGS.includes(element.tagName) ? '' : visibleText(element),
    collapse(element.getAttribute('placeholder') ?? ''),
    collapse(element.getAttribute('title') ?? ''),
  ];
  return candidates.find(candidate => candidate.length > 0) ?? '';
};

export const toSegmentName = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, MAX_NAME_LENGTH)
    .replace(/-+$/g, '');
